import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import BedroomParentIcon from '@mui/icons-material/BedroomParent';
import RoomBilling from './Invoice/RoomBilling';
import { API_GET_BOOKED_ROOMS } from '../../Util/URLConstants';
import Loader from '../../Components/Loader';
import AllocateRooms from './Options/AllocateRooms';
import { ROUTE_ENQUIRY_DETAILS, SESSION_USER_ID_KEY } from '../../Util/VMConstants';
import StorageFactory from '../../Util/StorageFactory';
import { align_center, align_row, VM_Colors } from '../../Styles/VMStyles';

function VMAllocateRooms({ initialCustomer = null, initialEvents = null, initialDisplayHeading = true, invoiceLocked = false }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Prefer props passed directly; otherwise fall back to location.state
  const customerFromLocation = location?.state?.customer ?? null;
  const eventsFromLocation = location?.state?.events ?? null;
  const displayHeadingFromLocation = location?.state?.displayHeading ?? true;

  const customer = initialCustomer ?? customerFromLocation;
  const events = initialEvents ?? eventsFromLocation;
  const displayHeading = initialDisplayHeading ?? displayHeadingFromLocation;

  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isBookingEnable, setIsBookingEnable] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const sessionFactory = useMemo(() => new StorageFactory('session'), []);
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);

  // Fetcher (accepts optional AbortSignal)
  const getBookedRoomsInfo = useCallback(async (signal) => {
    if (!userID || !customer?.appointment_id) {
      console.warn('Missing customer or user ID; skipping fetch.');
      return;
    }

    setErrorMsg('');
    setLoading(true);
    try {
      const url = `${API_GET_BOOKED_ROOMS}?user_id=${userID}&enquiry_id=${customer.appointment_id}`;
      const config = signal ? { signal } : {};
      const resp = await axios.get(url, config);
      const respData = resp?.data?.data;
      setRooms(respData?.rooms_info ?? []);
    } catch (err) {
      // axios with AbortController uses DOMException name "CanceledError" or xhr cancel token patterns
      if (err?.name === 'CanceledError' || axios.isCancel?.(err)) {
        // request cancelled — ignore
      } else {
        console.error('Error fetching booked rooms:', err);
        setErrorMsg('Failed to load room bookings. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [userID, customer?.appointment_id]);

  // refreshRooms returns a cleanup function (abort) if caller wants it
  const refreshRooms = useCallback(() => {
    if (!customer?.appointment_id) return;
    const controller = new AbortController();
    getBookedRoomsInfo(controller.signal);
    // return an abort function so effects can cleanup if needed
    return () => {
      try { controller.abort(); } catch (e) {}
    };
  }, [customer?.appointment_id, getBookedRoomsInfo]);

  // Initial load (and when userID or customer appointment changes)
  useEffect(() => {
    if (!customer?.appointment_id) return;
    const controller = new AbortController();
    getBookedRoomsInfo(controller.signal);
    return () => {
      try { controller.abort(); } catch (e) {}
    };
  }, [getBookedRoomsInfo, isBookingEnable,customer?.appointment_id, userID]);
    // }, [getBookedRoomsInfo, isBookingEnable,customer?.appointment_id, userID]);

  // If booking UI toggles to closed (false), refresh room list.
  // This ensures that if booking was cancelled or closed, we re-check server state.
  // useEffect(() => {
  //   if (isBookingEnable === false) {
  //     // call refreshRooms but we don't need to hold its abort because it's short-lived
  //     refreshRooms();
  //   }
  //   // only watch isBookingEnable (and refreshRooms reference)
  // }, [isBookingEnable, refreshRooms]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBookRoom = () => setIsBookingEnable(prev => !prev);

  const applyMargin = location?.pathname?.includes('roomsAllocation');

  if (!customer) {
    return (
      <Paper sx={{ padding: 2, ...(applyMargin ? { ml: { md: 5 } } : {}) }}>
        <Typography variant="h6" color="error">Error: Customer information is missing.</Typography>
        <Button
          onClick={() =>
            navigate('/' + ROUTE_ENQUIRY_DETAILS, { replace: true, state: { enquiry_id: customer?.appointment_id, navigate_from: 'roomsallocation' } })
          }
        >
          Go Back
        </Button>
      </Paper>
    );
  }

  return (
    <Paper sx={{ padding: 2, ...(applyMargin ? { ml: { md: 2 } } : {}) }}>
      {displayHeading &&
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center">
            <Button variant="outlined" size="small"
              onClick={() =>
                navigate('/' + ROUTE_ENQUIRY_DETAILS, { replace: true, state: { enquiry_id: customer.appointment_id, navigate_from: 'roomsallocation' } })
              }
              aria-label="Back">← Back</Button>
            <Typography variant="h4" sx={{ ml: 2 }}>Rooms Allocation</Typography>
          </Box>

          <Button
            variant="contained"
            onClick={handleBookRoom}
            disabled={invoiceLocked}
            aria-pressed={isBookingEnable}
            aria-label={isBookingEnable ? 'Show rooms data' : 'Open room booking'}
          >
            {isBookingEnable ? 'Rooms Data' : 'Room Book'}
          </Button>
        </Box>
      }

      {isBookingEnable && !invoiceLocked ? (
        <Box sx={{backgroundColor:VM_Colors.white2}}>
          {/* Pass onRoomAdded so AllocateRooms can tell parent to refresh */}
          <AllocateRooms
            customer={customer}
            handleBookRoom={handleBookRoom}
            onRoomAdded={() => {
              // close the booking UI and refresh parent list immediately
              setIsBookingEnable(false);
              // call refreshRooms explicitly (will fetch latest)
              refreshRooms();
            }}
          />
        </Box>
      ) : rooms.length > 0 ? (
        <RoomBilling bookedRoomsState={rooms} bookedEventState={events} displayHeading={displayHeading} readOnly={invoiceLocked} />
      ) : (
         <Box sx= {{...align_center,...align_row,
                  width: '100%', 
                //  width: 175,
                //  height: 260, // 180 + 44 + margin
                 flexDirection:'column',
                 minHeight:150,
                  justifyContent:'center',
                  textAlign:'center',
                //  gap:2 ,
                pt:5,
                 backgroundColor:VM_Colors.white2}}>
                              
                     <BedroomParentIcon sx={{ fontSize: 35,  backgroundColor:VM_Colors.tansparent,color:VM_Colors.dark_gradient}} />
                     <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1,mb:1 }}>
                         {errorMsg || 'No room bookings found.'}
                     </Typography>
                </Box>   
        
      )}

      <Loader loading={loading} />
    </Paper>
  );
}

export default VMAllocateRooms;
